import React, { useState, useEffect, useRef } from "react";
import { GitBranch, Plus, Check, Loader2, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { useStyleSessionStore } from "../../stores/styleSessionStore";

interface StyleBranchingProps {
  sessionId: string;
  onBranchSwitch?: (branch: string) => void;
}

export function StyleBranching({
  sessionId,
  onBranchSwitch,
}: StyleBranchingProps) {
  const {
    branches,
    currentBranch,
    isGitRepo,
    isBranchLoading,
    branchError,
    loadBranches,
    switchBranch,
    setBranchError,
  } = useStyleSessionStore();

  const [isOpen, setIsOpen] = useState(false);
  const [showNewBranchInput, setShowNewBranchInput] = useState(false);
  const [newBranchName, setNewBranchName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [switchingBranch, setSwitchingBranch] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sessionBranches = branches[sessionId] || [];
  const sessionCurrentBranch = currentBranch[sessionId] || null;
  const sessionIsGitRepo = isGitRepo[sessionId] || false;

  useEffect(() => {
    if (sessionId) {
      loadBranches(sessionId);
    }
  }, [sessionId, loadBranches]);

  const handleSwitchBranch = async (branchName: string) => {
    try {
      setSwitchingBranch(branchName);
      console.log(`🌿 Attempting to switch to branch: "${branchName}" for style session ${sessionId}`);
      await switchBranch(sessionId, branchName);
      if (!branchError) {
        console.log(`✅ Successfully switched to branch: "${branchName}"`);
        onBranchSwitch?.(branchName);
        setIsOpen(false);
      }
    } catch (error) {
      console.error("Failed to switch branch:", error);
    } finally {
      setSwitchingBranch(null);
    }
  };

  const createNewBranch = async () => {
    if (!newBranchName.trim()) return;

    try {
      setIsCreating(true);
      await switchBranch(sessionId, newBranchName.trim());
      if (!branchError) {
        onBranchSwitch?.(newBranchName.trim());
        setNewBranchName("");
        setShowNewBranchInput(false);
        setIsOpen(false);
      }
    } catch (error) {
      console.error("Failed to create branch:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleNewBranchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowNewBranchInput(true);
    // Focus the input after state update
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const cancelNewBranch = () => {
    setShowNewBranchInput(false);
    setNewBranchName("");
  };

  const handleNewBranchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      createNewBranch();
    } else if (e.key === "Escape") {
      cancelNewBranch();
    }
  };

  // Reset new branch input when dropdown closes
  useEffect(() => {
    if (!isOpen && showNewBranchInput) {
      cancelNewBranch();
    }
  }, [isOpen]);

  // Clear error when component unmounts or sessionId changes
  useEffect(() => {
    return () => {
      setBranchError(null);
    };
  }, [sessionId, setBranchError]);

  // Don't render if no git repo or no branches are available
  if (!sessionIsGitRepo || (sessionBranches.length === 0 && !isBranchLoading)) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-1.5 text-xs hover:bg-slate-200 dark:hover:bg-slate-700 gap-1 font-mono border border-transparent hover:border-muted transition-colors"
          disabled={isBranchLoading}
        >
          {isBranchLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <GitBranch className="h-3 w-3" />
          )}
          <span className="max-w-24 truncate">{sessionCurrentBranch || "main"}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 p-1">
        <div className="mb-2">
          <div className="text-xs text-muted-foreground px-2 py-2 border-b border-muted">
            Switch style branches
          </div>
        </div>

        {branchError && (
          <div className="px-2 py-1 mb-2">
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
              {branchError}
            </div>
          </div>
        )}

        <div className="max-h-40 overflow-y-auto">
          {sessionBranches.map((branch) => (
            <DropdownMenuItem
              key={branch.name}
              onClick={() => handleSwitchBranch(branch.name)}
              disabled={branch.isCurrent || switchingBranch === branch.name}
              className="flex items-center justify-between cursor-pointer py-2 px-2 rounded-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <div className="flex items-center flex-1 min-w-0">
                <GitBranch className="h-3 w-3 text-muted-foreground flex-shrink-0 mr-2" />
                <span className="truncate font-mono text-sm">
                  {branch.name}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {switchingBranch === branch.name && (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
                {branch.isCurrent && (
                  <Check className="h-3 w-3 text-green-600" />
                )}
              </div>
            </DropdownMenuItem>
          ))}
        </div>

        <div className="border-t border-muted mt-2 pt-2">
          {showNewBranchInput ? (
            <div className="px-2 py-1 space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  ref={inputRef}
                  placeholder="Enter branch name..."
                  value={newBranchName}
                  onChange={(e) => setNewBranchName(e.target.value)}
                  onKeyDown={handleNewBranchKeyPress}
                  className="h-7 text-xs font-mono flex-1"
                  disabled={isCreating}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={cancelNewBranch}
                  className="h-7 w-7 p-0 hover:bg-slate-200 dark:hover:bg-slate-700"
                  disabled={isCreating}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  onClick={createNewBranch}
                  disabled={!newBranchName.trim() || isCreating}
                  className="h-6 px-2 text-xs flex-1"
                >
                  {isCreating ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Plus className="h-3 w-3 mr-1" />
                  )}
                  {isCreating ? "Creating..." : "Create"}
                </Button>
              </div>
            </div>
          ) : (
            <DropdownMenuItem
              onClick={handleNewBranchClick}
              className="cursor-pointer py-2 px-2 rounded-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <Plus className="h-3 w-3 mr-2 text-muted-foreground" />
              <span className="text-sm">Create new branch</span>
            </DropdownMenuItem>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}